import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import { hasValue } from 'globalutils/fmUtils';
import PropTypes from 'prop-types';
import React, { lazy, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import TopActions from './TopActions';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Table = lazy(() => import(`globalcomponents/UI/Table/${layout}`));
const Icon = lazy(() => import(`globalcomponents/UI/Icon/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`).default;

const Component = ({
  apiStatus,
  callback,
  history,
  Styled,
  fetchInspections,
  bulkDownloadReport,
  inspections,
  list,
  changeRequest,
  isAdvancedDviRequired,
  isCustomDviRequired,
  logBook,
  clearApiStatus,
  i18n,
  topAction,
  getDocumentToken,
  filters,
  viewReport,
  request,
  pagination,
  setPagination,
  needSort
}) => {
  const [open, setOpen] = useState(false);
  const [modalObject, setModalObject] = useState({ header: '', content: () => null });

  const closeModal = () => setOpen(false);

  const beBaseUrl = window?.$environment?.BE_BASE_URL;
  const hasApiStatus = hasValue(apiStatus.status, 'api', fetchInspections);

  const information = hasApiStatus?.errorMessage
    ? <Message type="error" message={i18n.t(hasApiStatus.errorMessage)} />
    : i18n.t('common.tableEmptyMessage');

  const columns = [
    { id: 'date', label: i18n.t('accidents.dateAndTime'), needSorting: true },
    { id: 'vehicle', label: i18n.t('vehicles.vin'), needSorting: true },
    { id: 'driver', label: i18n.t('common.driverName'), needSorting: true },
    { id: 'type', label: i18n.t('inspections.inspectionType'), needSorting: needSort },
    { id: 'document', label: i18n.t('common.photos') },
    { id: 'report', label: i18n.t('inspections.inspectionReport') },
    { id: 'status', label: i18n.t('logs.status') }
  ];

  if (window.$environment.SERVICE_PROVIDER === 'edvir') {
    columns.splice(6, 1); // Remove 'status' column for 'edvir' provider
  }

  const viewPictureCallback = (type, data, currentItem) => {
    if (currentItem) {
      setModalObject({
        header: '',
        content: () => (
          <Carousel showIndicators={false} showThumbs={false} showStatus={false}>
            {currentItem.photos.map((photo) => (
              <div key={photo.files[0].id}>
                <img src={`${beBaseUrl}/document/open/${photo.files[0].id}?token=${data}`} />
                <p className="legend">
                  {`${photo?.sectionName}${photo?.info ? ` : ${photo?.info}` : ''}`}
                </p>
              </div>
            ))}
          </Carousel>
        )
      });
      setOpen(true);
    }
  };

  const viewPicture = (item) => {
    getDocumentToken({
      apiId: 'getDocToken',
      callback: viewPictureCallback,
      item
    });
  };

  const generateRows = () => {
    return inspections?.content?.map((item) => {
      const vinTrimmedValue = item?.vehicle
        ? `...${item.vehicle.slice(-8)}`
        : item.vehicle;

      return {
        ...item,
        vehicle: vinTrimmedValue,
        report: (
          <a
            className="viewReport"
            onClick={() => viewReport({ apiId: 'viewReportApi', data: item?.id })}
          >
            {i18n.t('inspections.viewReport')}
          </a>
        ),
        status: getStatusElement(item.violationType),
        document: getDocumentElement(item, viewPicture)
      };
    }) || [];
  };

  const getStatusElement = (violationType) => {
    const statusMap = {
      DISTANCE: <div className="circle_orange csadvirAlign" title="Minimum Distance condition is not satisfied"></div>,
      DURATION: <div className="circle_yellow csadvirAlign" title="Minimum Duration condition is not satisfied"></div>,
      BOTH: <div className="circle_red csadvirAlign" title="Minimum Distance & Minimum Duration conditions are not satisfied"></div>,
      NONE: '-',
      OK: <div className="circle_green csadvirAlign" title="Completed correctly"></div>
    };
    return statusMap[violationType] || <div className="wrapOpt"></div>;
  };

  const getDocumentElement = (item, viewPicture) => {
    if (item.photos.length) {
      return window.$environment.CURRENT_LAYOUT === 'SpireonNew' ? (
        <a onClick={() => viewPicture(item)}>{i18n.t('View')}</a>
      ) : (
        <Icon
          src={mediaUrls?.full}
          tableicon
          onClick={() => viewPicture(item)}
        />
      );
    }
    return window.$environment.CURRENT_LAYOUT === 'SpireonNew' ? (
      ''
    ) : (
      <div className="noPointer">
        <Icon src={mediaUrls?.notFull} tableicon />
      </div>
    );
  };

  return (
    <Styled.Wrapper>
      {topAction && (
        <TopActions
          {...{
            isAdvancedDviRequired,
            isCustomDviRequired,
            i18n,
            history,
            list,
            bulkDownloadReport,
            apiStatus,
            clearApiStatus,
            callback,
            Styled,
            changeRequest,
            hasApiStatus,
            filters,
            request,
            pagination,
            setPagination
          }}
        />
      )}
      <div className="quickFilter"></div>
      <Styled.TableWrapper>
        <div className="driverstatusTitle">
          <span className="spireonOldOnly">Inspections</span>
        </div>
        <Table
          type="server"
          defaultSortBy={needSort ? { sortBy: 'type', order: 'desc' } : null}
          changeRequest={changeRequest}
          baseRequest={request}
          title=""
          columns={columns}
          data={generateRows()}
          emptyMessage={information}
          itemsPerPage={window.$environment.SERVICE_PROVIDER === 'spireon' ? 10 : 15}
          totalRecords={inspections?.totalCount}
          actions={[]}
          i18n={i18n}
          pageinationReset={pagination}
          pagenationClicked={(e) => setPagination(!e)}
        />
      </Styled.TableWrapper>
      <Modal
        open={open}
        setOpen={setOpen}
        data={modalObject}
        onCloseModal={closeModal}
        background="black"
        inspectionPopup
        withCloseIcon
      />
    </Styled.Wrapper>
  );
};

Component.propTypes = {
  history: PropTypes.object.isRequired,
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  fetchViolations: PropTypes.func.isRequired,
  handleExportCsaViolations: PropTypes.func.isRequired
};

export default Component;